import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import '../../../../components/Common/IcdCodeSearchField/IcdCodeSearchField.css';
import { personPickerLabel } from '../shared/PatientCaregiverPickers';
import lookupService from '../../../../services/lookupService';
import { formatIcdOptionLabel, mergeIcdSearchResults } from '../../../../utils/patientMonitoringIcdUtils';
import AppDateInput from '../../../../components/Common/AppDateInput/AppDateInput';
import { FREQUENCIES } from './sectionBuilders';
import { getPath, setPath, toggleIn } from './planState';

const CHECK = (
  <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
    <path d="M2 8.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function OptionAccordion({ node, plan, readOnly, renderCheck }) {
  const groups = node.groups || [];
  const selectedIn = (group) => {
    const selected = getPath(plan, group.path || node.path) || [];
    return (group.items || []).filter((item) => selected.includes(item)).length;
  };
  const [openId, setOpenId] = useState(groups[0]?.id || '');

  return (
    <div className="acp-acc">
      {groups.map((group) => {
        const open = openId === group.id;
        const count = selectedIn(group);
        const path = group.path || node.path;
        const selected = getPath(plan, path) || [];
        return (
          <div key={group.id} className={`acp-acc-item${open ? ' is-open' : ''}`}>
            <button
              type="button"
              className="acp-acc-head"
              aria-expanded={open}
              onClick={() => setOpenId(open ? '' : group.id)}
            >
              <span className="acp-acc-title">{group.label}</span>
              <span className={`acp-acc-meta${count ? ' has' : ''}`}>
                {count ? `${count} selected` : `${(group.items || []).length} options`}
              </span>
              <ChevronDown className="acp-acc-chev" size={16} />
            </button>
            {open ? (
              <div className="acp-acc-body">
                <div className="acp-cklist">
                  {(group.items || []).map((item) => renderCheck(
                    item,
                    selected.includes(item),
                    path,
                    item,
                    group.tone
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function nodeHasPath(node, paths) {
  if (!node || !paths.length) return false;
  if (node.path && paths.includes(node.path)) return true;
  return (node.children || []).some((child) => nodeHasPath(child, paths));
}

function cardHasChange(node, changedPaths) {
  if (!node || !changedPaths?.length) return false;
  const pathHit = node.path && changedPaths.some((path) => (
    path === node.path || path.startsWith(`${node.path}.`) || node.path.startsWith(`${path}.`)
  ));
  if (pathHit) return true;
  if ((node.items || []).some((item) => item?.path && changedPaths.includes(item.path))) return true;
  if ((node.groups || []).some((group) => cardHasChange(group, changedPaths))) return true;
  return (node.children || []).some((child) => cardHasChange(child, changedPaths));
}

function PlanCard({ node, accent, renderNodes, invalidPaths, changedPaths = [], startOpen = false }) {
  const hasInvalid = nodeHasPath(node, invalidPaths);
  const hasChanged = cardHasChange(node, changedPaths);
  const [open, setOpen] = useState(!node.collapsible || startOpen || hasInvalid || hasChanged);
  useEffect(() => {
    if (hasInvalid || hasChanged) setOpen(true);
  }, [hasInvalid, hasChanged]);
  const copy = (
    <>
      <h3>{node.title}</h3>
      {node.subtitle ? <p>{node.subtitle}</p> : null}
    </>
  );
  if (!node.collapsible) {
    return (
      <section className={`acp-card${accent ? ' acp-card--accent' : ''}${node.roomy ? ' acp-card--roomy' : ''}`}>
        {node.title ? <div className="acp-card-head">{copy}</div> : null}
        <div className="acp-card-body">{renderNodes(node.children)}</div>
      </section>
    );
  }
  return (
    <section className={`acp-card acp-card--collapse${open ? ' is-open' : ''}${accent ? ' acp-card--accent' : ''}`}>
      <button
        type="button"
        className="acp-card-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="acp-card-toggle-copy">{copy}</span>
        <ChevronDown className="acp-acc-chev" size={16} />
      </button>
      {open ? <div className="acp-card-body">{renderNodes(node.children)}</div> : null}
    </section>
  );
}

function StaffMultiField({ node, plan, readOnly, invalid, changed, itemChanged, records, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedIds = (Array.isArray(getPath(plan, node.path)) ? getPath(plan, node.path) : [])
    .map((id) => String(id))
    .filter(Boolean);
  const options = Array.isArray(records) ? records : [];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const labelFor = (id) => {
    const row = options.find((option) => String(option.id) === String(id));
    return row ? personPickerLabel(row) : `ID ${id}`;
  };

  const writeIds = (nextIds) => {
    let nextPlan = setPath(plan, node.path, nextIds);
    if (node.namePath) {
      const names = nextIds.map((id) => labelFor(id)).filter((name) => name && !name.startsWith('ID ')).join(', ');
      nextPlan = setPath(nextPlan, node.namePath, names);
      if (node.signoffNamePath) nextPlan = setPath(nextPlan, node.signoffNamePath, names);
    }
    onChange(nextPlan);
  };

    const available = options
      .filter((option) => !selectedIds.includes(String(option.id)))
      .slice()
      .sort((a, b) => personPickerLabel(a).localeCompare(personPickerLabel(b)));

  return (
    <div className={`acp-field${invalid ? ' is-invalid' : ''}${changed ? ' is-changed' : ''}${node.span ? ` acp-span-${node.span}` : ''}`}>
      <label className="acp-label">
        {node.label}
        {node.required ? <span className="acp-req"> *</span> : null}
        {changed ? <span className="acp-changed-tag">Changed</span> : null}
      </label>
      <div className="acp-multi" ref={rootRef}>
        <div
          className={`acp-multi-trigger${invalid ? ' is-invalid' : ''}${readOnly ? ' is-locked' : ''}${open ? ' is-open' : ''}`}
          role="button"
          tabIndex={readOnly ? -1 : 0}
          onClick={() => { if (!readOnly) setOpen((value) => !value); }}
          onKeyDown={(event) => {
            if (readOnly) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen((value) => !value);
            }
          }}
        >
          {selectedIds.length === 0 ? (
            <span className="acp-multi-placeholder">{node.placeholder || 'Select'}</span>
          ) : selectedIds.map((id) => (
            <span key={id} className={`acp-multi-chip${itemChanged?.(id) ? ' is-changed' : ''}`}>
              <span>{labelFor(id)}</span>
              {readOnly ? null : (
                <button
                  type="button"
                  aria-label={`Remove ${labelFor(id)}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    writeIds(selectedIds.filter((item) => item !== id));
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
        {open && !readOnly ? (
          <ul className="acp-multi-list" role="listbox">
            {options.length === 0 ? (
              <li className="acp-multi-empty">No caregivers are available for this practice.</li>
            ) : available.length === 0 ? (
              <li className="acp-multi-empty">All caregivers are selected.</li>
            ) : available.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  className="acp-multi-option"
                  onClick={() => {
                    writeIds([...selectedIds, String(option.id)]);
                  }}
                >
                  {personPickerLabel(option)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {invalid ? <div className="acp-hint acp-hint--error">Select at least one caregiver.</div> : null}
    </div>
  );
}

const cmsDiagnosisCache = new Map();

function CmsDiagnosisField({ node, value, readOnly, invalid, changed, required, onChange }) {
  const locked = readOnly || Boolean(node.disabled);
  const terms = Array.isArray(node.searchTerms) && node.searchTerms.length ? node.searchTerms : ['arrhythmia'];
  const termsKey = terms.join('|');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const requestIdRef = useRef(0);
  const [catalog, setCatalog] = useState(() => cmsDiagnosisCache.get(termsKey) || []);
  const [catalogLoading, setCatalogLoading] = useState(!cmsDiagnosisCache.has(termsKey));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remote, setRemote] = useState([]);
  const [searching, setSearching] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [menuPos, setMenuPos] = useState(null);

  useEffect(() => {
    if (cmsDiagnosisCache.has(termsKey)) {
      setCatalog(cmsDiagnosisCache.get(termsKey));
      setCatalogLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCatalogLoading(true);
    const searchTerms = termsKey.split('|').filter(Boolean);
    Promise.all(searchTerms.map((term) => lookupService.searchIcdCodes(term).catch(() => null)))
      .then((responses) => {
        if (cancelled) return;
        const merged = responses.reduce(
          (acc, res) => mergeIcdSearchResults(acc, Array.isArray(res?.data) ? res.data : []),
          []
        );
        merged.sort((a, b) => formatIcdOptionLabel(a).localeCompare(formatIcdOptionLabel(b)));
        cmsDiagnosisCache.set(termsKey, merged);
        setCatalog(merged);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, [termsKey]);

  const typed = String(query || '').trim();
  const matches = useMemo(() => {
    const q = typed.toLowerCase();
    const pool = mergeIcdSearchResults(remote, catalog);
    const filtered = q
      ? pool.filter((option) => formatIcdOptionLabel(option).toLowerCase().includes(q))
      : pool;
    return filtered.slice(0, 40).map((option) => formatIcdOptionLabel(option)).filter(Boolean);
  }, [catalog, remote, typed]);
  const exactMatch = matches.some((label) => label.toLowerCase() === typed.toLowerCase());
  const customLabel = typed && !exactMatch ? typed : '';
  const menuItems = customLabel ? [...matches, customLabel] : matches;

  useEffect(() => {
    if (!open || locked) return undefined;
    const q = typed;
    if (q.length < 2) {
      requestIdRef.current += 1;
      setRemote([]);
      setSearching(false);
      return undefined;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await lookupService.searchIcdCodes(q);
        if (requestId !== requestIdRef.current) return;
        setRemote(Array.isArray(res?.data) ? res.data : []);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setRemote([]);
      } finally {
        if (requestId === requestIdRef.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, open, locked]);

  useEffect(() => {
    setHighlight(0);
  }, [typed, matches.length, customLabel]);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return undefined;
    }
    const update = (event) => {
      if (event?.target && menuRef.current?.contains(event.target)) return;
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const maxHeight = Math.min(240, Math.max(spaceBelow, 140));
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, menuItems.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (wrapRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      const next = String(query || '').trim();
      if (next !== String(value || '').trim()) onChange(next);
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, query, value, onChange]);

  const choose = (label) => {
    onChange(label);
    setQuery(label);
    setOpen(false);
  };

  const menu = open && !locked && menuPos && typeof document !== 'undefined'
    ? createPortal(
      <ul
        ref={menuRef}
        className="icd-search-dropdown"
        role="listbox"
        style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width, maxHeight: menuPos.maxHeight }}
      >
        {(catalogLoading || searching) && menuItems.length === 0 ? (
          <li className="icd-search-empty">Searching diagnoses…</li>
        ) : null}
        {!catalogLoading && !searching && menuItems.length === 0 ? (
          <li className="icd-search-empty">Type a diagnosis to search or add your own</li>
        ) : null}
        {menuItems.map((label, index) => {
          const isCustom = label === customLabel;
          return (
            <li key={isCustom ? `custom-${label}` : label}>
              <button
                type="button"
                role="option"
                className={`icd-search-option${index === highlight ? ' is-active' : ''}${label === value ? ' is-selected' : ''}`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(label)}
              >
                {isCustom ? `Add "${label}"` : label}
              </button>
            </li>
          );
        })}
      </ul>,
      document.body
    )
    : null;

  return (
    <div className={`acp-field${invalid ? ' is-invalid' : ''}${changed ? ' is-changed' : ''}${node.span ? ` acp-span-${node.span}` : ''}`}>
      <label className="acp-label">
        {node.label}
        {required ? <span className="acp-req"> *</span> : null}
        {changed ? <span className="acp-changed-tag">Changed</span> : null}
      </label>
      <div ref={wrapRef}>
        <input
          ref={inputRef}
          type="text"
          className={`acp-input${node.disabled ? ' is-locked' : ''}${invalid ? ' is-invalid' : ''}`}
          aria-invalid={invalid || undefined}
          aria-autocomplete="list"
          aria-expanded={open}
          role="combobox"
          autoComplete="off"
          disabled={locked}
          placeholder={node.placeholder || 'Search or type a diagnosis'}
          value={open ? query : value}
          onFocus={() => {
            if (locked) return;
            setQuery(String(value || ''));
            setOpen(true);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setHighlight((index) => Math.min(index + 1, Math.max(menuItems.length - 1, 0)));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const picked = menuItems[highlight] || typed;
              if (picked) choose(picked);
            } else if (event.key === 'Escape') {
              setQuery(String(value || ''));
              setOpen(false);
            }
          }}
        />
      </div>
      {menu}
      {invalid ? <div className="acp-hint acp-hint--error">This field is required.</div> : null}
      {locked ? null : <div className="acp-hint">Search the diagnosis list, or type your own and choose Add.</div>}
    </div>
  );
}

function visible(node, plan, ctx) {
  if (typeof node?.showWhen === 'function' && !node.showWhen(plan, ctx)) return false;
  return true;
}

function DiseasePlanForm({
  disease,
  plan,
  ctx,
  sectionId,
  program,
  readOnly,
  invalidPaths = [],
  changes = null,
  onChange,
  staffOptions = {},
}) {
  const sections = disease.sections(program || 'CCM');
  const active = sections.find((item) => item.id === sectionId) || sections[0];
  const invalidKey = invalidPaths.join('|');
  useEffect(() => {
    if (!invalidKey) return;
    const field = document.querySelector('.dcp-modal .acp-field.is-invalid');
    field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [invalidKey]);
  if (!active) return null;

  const setValue = (path, value) => onChange(setPath(plan, path, value));
  const toggle = (path, item) => onChange(toggleIn(plan, path, item));
  const pathChanged = (path) => Boolean(path && changes?.paths?.has(path));
  const itemChanged = (path, item) => Boolean(changes?.items?.has(`${path}\0${item}`));
  const rowChanged = (path, row) => Boolean(row?.id != null && changes?.rowIds?.has(`${path}\0${row.id}`));
  const changedPathList = changes?.paths ? [...changes.paths] : [];

  const renderNodes = (nodes) => {
    let firstCollapseMarked = false;
    return (nodes || []).filter((node) => visible(node, plan, ctx)).map((node, index) => {
      const startOpen = node.type === 'card' && node.collapsible && !firstCollapseMarked;
      if (startOpen) firstCollapseMarked = true;
      return (
        <React.Fragment key={node.path || node.title || node.type || index}>
          {renderNode(node, startOpen)}
        </React.Fragment>
      );
    });
  };

  const renderNode = (node, startOpen = false) => {
    if (node.type === 'card') {
      const accent = node.accent || (typeof node.accentWhen === 'function' && node.accentWhen(plan, ctx));
      return (
        <PlanCard
          node={node}
          accent={accent}
          renderNodes={renderNodes}
          invalidPaths={invalidPaths}
          changedPaths={changedPathList}
          startOpen={startOpen}
        />
      );
    }
    if (node.type === 'grid') {
      return <div className={`acp-grid acp-grid-${node.cols || 2}${node.className ? ` ${node.className}` : ''}`}>{renderNodes(node.children)}</div>;
    }
    if (node.type === 'heading') return <div className="acp-section-label">{node.text}</div>;
    if (node.type === 'field') return renderField(node);
    if (node.type === 'chips') return renderChips(node);
    if (node.type === 'accordion') return renderAccordion(node);
    if (node.type === 'pills') return renderPills(node);
    if (node.type === 'score') return renderScore(node);
    if (node.type === 'rows') return renderRows(node);
    if (node.type === 'meds') return renderMeds(node);
    if (node.type === 'monitors') return renderMonitors(node);
    if (node.type === 'attests') return renderAttests(node);
    if (node.type === 'readout') return <p className="acp-readout">{node.text}</p>;
    if (node.type === 'empty') {
      return (
        <div className="acp-empty">
          <p>{node.title}</p>
          <span>{node.text}</span>
        </div>
      );
    }
    return null;
  };

  const renderField = (node) => {
    if (node.optionSource === 'cmsIcd') {
      const required = node.required || (typeof node.requiredWhen === 'function' && node.requiredWhen(plan, ctx));
      return (
        <CmsDiagnosisField
          node={node}
          value={getPath(plan, node.path) ?? ''}
          readOnly={readOnly}
          invalid={invalidPaths.includes(node.path)}
          changed={pathChanged(node.path)}
          required={required}
          onChange={(nextValue) => onChange(setPath(plan, node.path, nextValue))}
        />
      );
    }
    if (node.input === 'multiselect') {
      const records = node.optionSource === 'practiceCaregivers'
        ? staffOptions.caregiverRecords
        : node.optionSource === 'practiceProviders'
          ? staffOptions.providerRecords
          : [];
      return (
        <StaffMultiField
          node={node}
          plan={plan}
          readOnly={readOnly}
          invalid={invalidPaths.includes(node.path)}
          changed={pathChanged(node.path)}
          itemChanged={(id) => itemChanged(node.path, id)}
          records={records}
          onChange={onChange}
        />
      );
    }
    const value = getPath(plan, node.path) ?? '';
    const required = node.required || (typeof node.requiredWhen === 'function' && node.requiredWhen(plan, ctx));
    const invalid = invalidPaths.includes(node.path);
    const changed = pathChanged(node.path);
    const controlProps = {
      className: `acp-input${node.disabled ? ' is-locked' : ''}${invalid ? ' is-invalid' : ''}`,
      'aria-invalid': invalid || undefined,
      disabled: readOnly || node.disabled,
      value,
      onChange: (event) => {
        const rawValue = event.target.value;
        const nextValue = node.input === 'number' ? rawValue.replace(/\D/g, '') : rawValue;
        let nextPlan = setPath(plan, node.path, nextValue);
        if (node.optionSource === 'practiceProviders') {
          const match = (staffOptions.providerRecords || []).find(
            (row) => personPickerLabel(row) === nextValue
          );
          nextPlan = setPath(nextPlan, 'team.pcpUserId', match?.id ?? '');
          nextPlan = setPath(
            nextPlan,
            'signoff.providerName',
            String(nextValue || '').replace(/\s*\(inactive\)\s*$/i, '').trim()
          );
        }
        onChange(nextPlan);
      },
    };
    let control = null;
    if (node.input === 'textarea') {
      control = <textarea {...controlProps} rows={node.rows || 3} placeholder={node.placeholder || ''} />;
    } else if (node.input === 'select') {
      const sourceOptions = node.optionSource === 'practiceCaregivers'
        ? staffOptions.caregivers
        : node.optionSource === 'practiceProviders'
          ? staffOptions.providers
          : node.options;
      const selectOptions = (Array.isArray(sourceOptions) ? sourceOptions : [])
        .map((option) => String(option || '').trim())
        .filter(Boolean);
      if (value && !selectOptions.includes(String(value))) {
        selectOptions.unshift(String(value));
      }
      control = (
        <select {...controlProps}>
          <option value="">{node.placeholder || 'Select'}</option>
          {selectOptions.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    } else if (node.input === 'date') {
      control = (
        <AppDateInput
          className={`acp-input${node.disabled ? ' is-locked' : ''}${invalid ? ' is-invalid' : ''}`}
          value={value}
          disabled={readOnly || node.disabled}
          showCalendarIcon
          placeholder="mm/dd/yyyy"
          onChange={(nextValue) => onChange(setPath(plan, node.path, nextValue || ''))}
        />
      );
    } else {
      control = (
        <input
          {...controlProps}
          type="text"
          inputMode={node.input === 'number' ? 'numeric' : undefined}
          placeholder={node.placeholder || ''}
        />
      );
    }
    return (
      <div className={`acp-field${invalid ? ' is-invalid' : ''}${changed ? ' is-changed' : ''}${node.span ? ` acp-span-${node.span}` : ''}`}>
        <label className="acp-label">
          {node.label}
          {required ? <span className="acp-req"> *</span> : null}
          {changed ? <span className="acp-changed-tag">Changed</span> : null}
        </label>
        {control}
        {invalid ? <div className="acp-hint acp-hint--error">This field is required.</div> : null}
        {node.hint ? <div className="acp-hint">{node.hint}</div> : null}
      </div>
    );
  };

  const renderAccordion = (node) => (
    <OptionAccordion
      node={node}
      plan={plan}
      readOnly={readOnly}
      renderCheck={(label, on, path, item, tone) => renderCheck(label, on, () => toggle(path, item), tone, itemChanged(path, item))}
    />
  );

  const renderCheck = (label, on, onToggle, tone, changed = false) => (
    <button
      type="button"
      className={`acp-check${on ? ' is-on' : ''}${tone ? ` tone-${tone}` : ''}${changed ? ' is-changed' : ''}`}
      aria-pressed={on ? 'true' : 'false'}
      disabled={readOnly}
      onClick={() => { if (!readOnly) onToggle(); }}
    >
      <span className="acp-box" aria-hidden="true">{CHECK}</span>
      <span>{label}</span>
    </button>
  );

  const renderChips = (node) => {
    const selected = getPath(plan, node.path) || [];
    return (
      <div className={`acp-field${pathChanged(node.path) ? ' is-changed' : ''}`}>
        {node.label && node.label !== node.path ? (
          <label className="acp-label">
            {node.label}
            {pathChanged(node.path) ? <span className="acp-changed-tag">Changed</span> : null}
          </label>
        ) : null}
        <div className={`acp-chips${node.stack ? ' acp-chips--stack' : ''}${node.nowrap ? ' acp-chips--line' : ''}`}>
          {(node.items || []).map((item) => renderCheck(item, selected.includes(item), () => toggle(node.path, item), node.tone, itemChanged(node.path, item)))}
        </div>
      </div>
    );
  };

  const renderPills = (node) => {
    const current = getPath(plan, node.path) || '';
    return (
      <div className="acp-chips acp-chips--pills">
        {(node.items || []).map((item) => (
          <button
            key={item}
            type="button"
            className={`acp-pill${current === item ? ' is-on' : ''}${pathChanged(node.path) && current === item ? ' is-changed' : ''}`}
            disabled={readOnly}
            onClick={() => { if (!readOnly) setValue(node.path, item); }}
          >
            {item}
          </button>
        ))}
      </div>
    );
  };

  const renderScore = (node) => {
    const selected = getPath(plan, node.path) || [];
    const total = (node.items || []).reduce((sum, row) => (selected.includes(row.id) ? sum + row.pts : sum), 0);
    return (
      <>
        <div className="acp-score-list">
          {(node.items || []).map((row) => (
            <div className="acp-score-row" key={row.id}>
              {renderCheck(row.label, selected.includes(row.id), () => toggle(node.path, row.id), undefined, itemChanged(node.path, row.id))}
              <span className="acp-pts">{row.pts}</span>
            </div>
          ))}
        </div>
        <div className="acp-total">
          <span>{node.totalLabel}</span>
          <strong>{total}</strong>
        </div>
      </>
    );
  };

  const renderRows = (node) => {
    const rows = getPath(plan, node.path) || [];
    const patchRow = (id, key, value) => {
      onChange(setPath(plan, node.path, rows.map((row) => (row.id === id ? { ...row, [key]: value } : row))));
    };
    const removeRow = (id) => onChange(setPath(plan, node.path, rows.filter((row) => row.id !== id)));
    const addRow = () => {
      const blank = { id: `g${Date.now()}` };
      (node.columns || []).forEach((column) => { blank[column.key] = ''; });
      onChange(setPath(plan, node.path, [...rows, blank]));
    };
    return (
      <>
        <div className="acp-table">
          <div className="acp-tr acp-th">
            {(node.columns || []).map((column) => <div key={column.key}>{column.label}</div>)}
            <div />
          </div>
          {rows.map((row) => (
            <div className={`acp-tr${rowChanged(node.path, row) ? ' is-changed' : ''}`} key={row.id}>
              {(node.columns || []).map((column) => (
                <input
                  key={column.key}
                  className="acp-input"
                  type={column.input || 'text'}
                  placeholder={column.placeholder || ''}
                  disabled={readOnly}
                  value={row[column.key] || ''}
                  onChange={(event) => patchRow(row.id, column.key, event.target.value)}
                />
              ))}
              {readOnly ? <span /> : (
                <button type="button" className="acp-x" aria-label="Remove goal" onClick={() => removeRow(row.id)}>×</button>
              )}
            </div>
          ))}
        </div>
        {readOnly ? null : (
          <button
            type="button"
            className={`acp-add${rows.length === 0 ? ' acp-add--center' : ''}`}
            onClick={addRow}
          >
            + {node.addLabel || 'Add'}
          </button>
        )}
      </>
    );
  };

  const renderMeds = (node) => {
    const rows = getPath(plan, node.path) || [];
    const patchRow = (id, key, value) => {
      onChange(setPath(plan, node.path, rows.map((row) => (row.id === id ? { ...row, [key]: value } : row))));
    };
    const addMedication = () => onChange(setPath(plan, node.path, [...rows, { id: `m${Date.now()}`, name: '', dose: '', freq: '', note: '' }]));
    const addButton = (centered) => (readOnly ? null : (
      <button type="button" className={`acp-add${centered ? ' acp-add--center' : ''}`} onClick={addMedication}>
        Add medication
      </button>
    ));
    return (
      <>
        <div className="acp-table acp-table--med">
          <div className="acp-tr acp-th">
            <div>Medication</div><div>Dose</div><div>Frequency</div><div>Indication / notes</div><div />
          </div>
          {rows.length === 0 ? (
            <div className="acp-empty acp-empty--row">
              {addButton(true)}
              <p>No medications added.</p>
            </div>
          ) : null}
          {rows.map((row) => (
            <div className={`acp-tr${rowChanged(node.path, row) ? ' is-changed' : ''}`} key={row.id}>
              {['name', 'dose', 'freq', 'note'].map((key) => (
                <input
                  key={key}
                  className="acp-input"
                  disabled={readOnly}
                  placeholder={key === 'name' ? 'Name' : key === 'dose' ? '5 mg' : key === 'freq' ? 'Twice daily' : 'Indication'}
                  value={row[key] || ''}
                  onChange={(event) => patchRow(row.id, key, event.target.value)}
                />
              ))}
              {readOnly ? <span /> : (
                <button
                  type="button"
                  className="acp-x"
                  aria-label="Remove medication"
                  onClick={() => onChange(setPath(plan, node.path, rows.filter((item) => item.id !== row.id)))}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
        {rows.length > 0 ? addButton(false) : null}
      </>
    );
  };

  const renderMonitors = (node) => {
    const current = getPath(plan, node.path) || {};
    const frequencies = disease.frequencies || FREQUENCIES;
    return (
      <div className="acp-monitor">
        {(node.items || []).map((item) => {
          const value = current[item.id] || '';
          return (
            <div className={`acp-monitor-row${value ? ' is-on' : ''}${pathChanged(`${node.path}.${item.id}`) ? ' is-changed' : ''}`} key={item.id}>
              <div className="acp-monitor-label">
                <strong>{item.label}</strong>
                <span>{item.method}</span>
              </div>
              <select
                className="acp-input"
                disabled={readOnly}
                value={value}
                onChange={(event) => {
                  const next = { ...current };
                  if (!event.target.value) delete next[item.id];
                  else next[item.id] = event.target.value;
                  setValue(node.path, next);
                }}
              >
                <option value="">Not monitored</option>
                {frequencies.map((freq) => <option key={freq} value={freq}>{freq}</option>)}
              </select>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAttests = (node) => (
    <div className="acp-attest">
      {(node.items || []).map((item) => renderCheck(item.label, Boolean(getPath(plan, item.path)), () => setValue(item.path, !getPath(plan, item.path)), undefined, pathChanged(item.path)))}
    </div>
  );

  return <div className="acp-section">{renderNodes(active.cards)}</div>;
}

export function sectionList(disease, program) {
  return disease.sections(program);
}

export default DiseasePlanForm;
